export interface ShareContent {
  text: string;
  url: string;
  hashtags?: string[];
}

export function generateShareContent(
  goalText: string,
  completionDate?: string
): ShareContent {
  const baseText = completionDate 
    ? `Just completed my daily goal: "${goalText}" 🎯 Staying accountable with @OneWinToday!`
    : `Working on my daily goal: "${goalText}" 🎯 Accountability matters with @OneWinToday!`;

  return {
    text: baseText,
    url: 'https://onewin.today',
    hashtags: ['accountability', 'goals', 'productivity', 'OneWinToday']
  };
}

export function generateLinkedInShareUrl(content: ShareContent): string {
  const params = new URLSearchParams({
    mini: 'true',
    url: content.url,
    title: 'OneWin.Today - Daily Goal Achievement',
    summary: content.text,
    source: 'OneWin.Today'
  });

  return `https://www.linkedin.com/sharing/share-offsite/?${params.toString()}`;
}

export function generateTwitterShareUrl(content: ShareContent): string {
  const tweetText = content.hashtags 
    ? `${content.text} ${content.hashtags.map(tag => `#${tag}`).join(' ')}`
    : content.text;

  const params = new URLSearchParams({
    text: tweetText,
    url: content.url
  });

  return `https://twitter.com/intent/tweet?${params.toString()}`;
}

export function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text)
      .then(() => true)
      .catch(() => false);
  } else {
    // Fallback for older browsers or non-HTTPS contexts
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      return Promise.resolve(successful);
    } catch (err) {
      document.body.removeChild(textArea);
      return Promise.resolve(false);
    }
  }
}

export function openShareWindow(url: string, platform: string): void {
  const width = 600;
  const height = 400;
  const left = (window.innerWidth - width) / 2;
  const top = (window.innerHeight - height) / 2;

  const shareWindow = window.open(
    url,
    `share-${platform}`,
    `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
  );

  // Focus the share window if it opened successfully
  if (shareWindow) {
    shareWindow.focus();
  }
}

export function validateShareContent(content: ShareContent): boolean {
  return !!(
    content.text && 
    content.text.length > 0 && 
    content.text.length <= 280 && // Twitter limit
    content.url && 
    isValidUrl(content.url)
  );
}

function isValidUrl(string: string): boolean {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
}

export function truncateShareText(text: string, maxLength: number = 240): string {
  if (text.length <= maxLength) return text;
  
  const truncated = text.substring(0, maxLength - 3);
  const lastSpace = truncated.lastIndexOf(' ');
  
  return lastSpace > 0 
    ? truncated.substring(0, lastSpace) + '...'
    : truncated + '...';
}